import { useUpdateTagFavoriteMutation } from '@Queries/index';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { Column } from '../../../../components-v2/PageLayout/Layout';
import { ButtonBase } from '../../../../components/Button';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../components/Popover';
import { styleSheet } from '../styles';
import { TagFavoriteForIndicator } from './TagFavoriteIndicator';

interface TagFavoriteColumnProps {
	tag: Api.IAccountTag;
	impersonationContext?: Api.IImpersonationContext;
	disabled?: boolean;
	refreshTags: () => void;
}

export const TagFavoriteColumn: React.FC<TagFavoriteColumnProps> = ({
	tag,
	impersonationContext,
	disabled = false,
	refreshTags,
}) => {
	const [isUpdating, setIsUpdating] = React.useState(false);
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();

	const updateTagFavoriteMutation = useUpdateTagFavoriteMutation({
		impersonationContext,
		onSuccess: () => {
			setIsUpdating(false);
			refreshTags();
		},
		onError: (error: Api.IOperationResultNoValue) => {
			setIsUpdating(false);
			errorMessages.pushApiError(error);
		},
	});

	// Check if user has admin rights for this tag
	const isAdmin = userSession.userRole === 'admin' || userSession.userRole === 'superAdmin';

	// Determine if user can change favorite status
	const canChangeFavoriteFor =
		tag.favoriteFor !== Api.FavoriteFor.Account || (tag.favoriteFor === Api.FavoriteFor.Account && isAdmin);

	const toggleFavorite = () => {
		if (canChangeFavoriteFor) {
			const favoriteFor = tag.favoriteFor === Api.FavoriteFor.User ? Api.FavoriteFor.None : Api.FavoriteFor.User;
			saveFavoriteFor(favoriteFor);
		}
	};

	const selectFavorite = (favorite: Api.FavoriteFor) => () => {
		saveFavoriteFor(favorite);
	};

	const saveFavoriteFor = (favoriteFor: Api.FavoriteFor) => {
		setIsUpdating(true);
		updateTagFavoriteMutation.mutate({
			tagId: tag.id,
			favoriteFor,
		});
	};

	const renderCurrentFavorite = () => {
		const isDisabled = !canChangeFavoriteFor;

		let title: string;
		if (isDisabled) {
			title = 'Shared favorite tag set by account admin';
		} else {
			switch (tag.favoriteFor) {
				case Api.FavoriteFor.Account:
					title = 'Shared favorite';
					break;
				case Api.FavoriteFor.User:
					title = 'Personal favorite';
					break;
				case Api.FavoriteFor.None:
				default:
					title = 'Click to make tag a favorite';
					break;
			}
		}

		return (
			<ButtonBase title={title} onClick={toggleFavorite} disabled={disabled || isUpdating}>
				<TagFavoriteForIndicator favoriteFor={tag.favoriteFor} />
			</ButtonBase>
		);
	};

	const renderFavoriteDropDown = () => {
		return (
			<Popover>
				<PopoverTrigger disabled={disabled} className={css(disabled && styleSheet.cursorNotAllowed)}>
					<TagFavoriteForIndicator favoriteFor={tag.favoriteFor} />
				</PopoverTrigger>
				<PopoverContent align='start'>
					<Column>
						<ButtonBase
							onClick={selectFavorite(Api.FavoriteFor.User)}
							className={css(styleSheet.favoriteOption)}
							disabled={isUpdating}
						>
							<TagFavoriteForIndicator favoriteFor={Api.FavoriteFor.User} />
							<span>Personal favorite</span>
						</ButtonBase>
						<ButtonBase
							onClick={selectFavorite(Api.FavoriteFor.Account)}
							className={css(styleSheet.favoriteOption)}
							disabled={isUpdating}
						>
							<TagFavoriteForIndicator favoriteFor={Api.FavoriteFor.Account} />
							<span>Shared favorite</span>
						</ButtonBase>
						<ButtonBase
							onClick={selectFavorite(Api.FavoriteFor.None)}
							className={css(styleSheet.favoriteOption)}
							disabled={isUpdating}
						>
							<TagFavoriteForIndicator favoriteFor={Api.FavoriteFor.None} />
							<span>Remove from favorites</span>
						</ButtonBase>
					</Column>
				</PopoverContent>
			</Popover>
		);
	};

	if (isUpdating) {
		return (
			<div className={css(styleSheet.favoriteCol, styleSheet.favorite)}>
				<LoadingSpinner type='tiny' />
			</div>
		);
	}

	if (isAdmin) {
		return renderFavoriteDropDown();
	}

	return renderCurrentFavorite();
};
